import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../../api/client'
import type {
  BusinessService,
  ConfigurationItem,
  Nis2Incident,
  Nis2IncidentInput,
  Nis2IncidentStep,
} from '../../api/types'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { Card, EmptyState, ErrorBanner, Spinner, StatCard, formatDateTime } from '../common'
import { DataTable, type Column } from '../DataTable'

/**
 * The NIS2 incident register (D.Lgs. 138/2024, art. 25): what a CISO needs in
 * the first 72 hours. Every significant incident runs three clocks from the
 * moment of becoming aware of it — early warning to CSIRT Italia within 24 h,
 * notification within 72 h, final report within one month of the
 * notification. The server computes deadlines and states; this view keeps the
 * countdowns live (one tick a minute) and records what was sent and when.
 */

type Passo = Nis2IncidentStep['step']
type Tono = 'ok' | 'warn' | 'error' | 'info' | 'muted'
type Rif = { id: string; name: string }
type Significato = '' | 'si' | 'no'

const ORA = 3_600_000
const PASSI: Passo[] = ['early_warning', 'notification', 'final_report']

/** The update field that records when each step was sent. */
const CAMPO_PASSO = {
  early_warning: 'early_warning_at',
  notification: 'notification_at',
  final_report: 'final_report_at',
} as const satisfies Record<Passo, keyof Nis2IncidentInput>

/** Hours before the deadline when the server e-mails its reminder: inside it the clock turns amber. */
const SOGLIA_ORE: Record<Passo, number> = { early_warning: 6, notification: 12, final_report: 7 * 24 }

/** The timeline line written when a recorded sending is cleared. Stored in
 *  Italian like the server's own lines, translated on display. */
const ANNULLATO: Record<Passo, string> = {
  early_warning: 'pre-notifica: invio annullato',
  notification: 'notifica: invio annullato',
  final_report: 'relazione finale: invio annullato',
}

const COLORE_TONO: Record<Tono, string> = {
  ok: 'var(--ok)',
  warn: 'var(--warn)',
  error: 'var(--error)',
  info: 'var(--info)',
  muted: 'var(--divider)',
}

// datetime-local is not in the console's input rule yet: same look by hand.
const STILE_DATA_ORA: CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: 14,
  fontFamily: 'inherit',
}

// ------------------------------------------------------------------ labels
// Literal t() calls: the translation guard does not see t(variable).

function nomePasso(p: Passo): string {
  switch (p) {
    case 'early_warning': return t('Pre-notifica')
    case 'notification': return t('Notifica')
    default: return t('Relazione finale')
  }
}

function terminePasso(p: Passo): string {
  switch (p) {
    case 'early_warning': return t('entro 24 h dalla presa di conoscenza')
    case 'notification': return t('entro 72 h dalla presa di conoscenza')
    default: return t('entro un mese dalla notifica')
  }
}

function cosaInviare(p: Passo): string {
  switch (p) {
    case 'early_warning':
      return t('Se si sospetta che l’incidente sia causato da atti illegittimi o malevoli e se può avere un impatto transfrontaliero.')
    case 'notification':
      return t('Aggiorna la pre-notifica: valutazione iniziale dell’incidente, gravità e impatto, indicatori di compromissione se disponibili.')
    default:
      return t('Descrizione dettagliata con gravità e impatto, tipo di minaccia o causa di fondo, misure di attenuazione adottate e in corso, impatto transfrontaliero.')
  }
}

function nomeStato(s: string): string {
  switch (s) {
    case 'aperto': return t('aperto')
    case 'contenuto': return t('contenuto')
    case 'chiuso': return t('chiuso')
    default: return s
  }
}

function tonoStato(s: string): Tono {
  return s === 'aperto' ? 'error' : s === 'contenuto' ? 'warn' : 'ok'
}

function nomeGravita(s: string): string {
  switch (s) {
    case 'low': return t('bassa')
    case 'medium': return t('media')
    case 'high': return t('alta')
    case 'critical': return t('critica')
    default: return s
  }
}

function tonoGravita(s: string): Tono {
  return s === 'critical' ? 'error' : s === 'high' ? 'warn' : s === 'medium' ? 'info' : 'muted'
}

function nomeSignificato(v: boolean | null): string {
  return v === null ? t('da valutare') : v ? t('significativo') : t('non significativo')
}

function tonoSignificato(v: boolean | null): Tono {
  return v === null ? 'warn' : v ? 'error' : 'muted'
}

/** The server writes its timeline lines in Italian: the known ones are translated. */
function testoVoce(testo: string): string {
  switch (testo) {
    case 'incidente registrato': return t('incidente registrato')
    case 'pre-notifica inviata': return t('pre-notifica inviata')
    case 'notifica inviata': return t('notifica inviata')
    case 'relazione finale inviata': return t('relazione finale inviata')
    case 'pre-notifica: invio annullato': return t('pre-notifica: invio annullato')
    case 'notifica: invio annullato': return t('notifica: invio annullato')
    case 'relazione finale: invio annullato': return t('relazione finale: invio annullato')
  }
  const stato = /^stato: (aperto|contenuto|chiuso)$/.exec(testo)
  return stato ? t('stato: {stato}', { stato: nomeStato(stato[1]) }) : testo
}

function Badge({ tono, children }: { tono: Tono; children: ReactNode }) {
  return <span className={`badge badge--${tono}`}>{children}</span>
}

// -------------------------------------------------------------------- time

/** Re-renders once a minute so the countdowns move on their own. */
function useAdesso(): number {
  const [adesso, setAdesso] = useState(() => Date.now())
  useEffect(() => {
    const timer = window.setInterval(() => setAdesso(Date.now()), 60_000)
    return () => window.clearInterval(timer)
  }, [])
  return adesso
}

/** Hours from now to the step's deadline (negative = overdue); null without a deadline. */
function oreAllaScadenza(p: Nis2IncidentStep, adesso: number): number | null {
  if (!p.due_at) return null
  const ms = Date.parse(p.due_at)
  return Number.isNaN(ms) ? null : (ms - adesso) / ORA
}

/** The step's state now: the server computed it at load time, and a running
 *  clock may have expired since. */
function statoOra(p: Nis2IncidentStep, adesso: number): string {
  if (p.state === 'in_corso') {
    const ore = oreAllaScadenza(p, adesso)
    if (ore !== null && ore < 0) return 'scaduto'
  }
  return p.state
}

function fra(ore: number): string {
  if (ore >= 48) return t('fra {n} giorni', { n: Math.floor(ore / 24) })
  if (ore >= 1) return t('fra {n} h', { n: Math.floor(ore) })
  return t('fra {n} min', { n: Math.max(0, Math.floor(ore * 60)) })
}

function scadutaDa(ore: number): string {
  const h = -ore
  if (h >= 48) return t('scaduta da {n} giorni', { n: Math.floor(h / 24) })
  if (h >= 1) return t('scaduta da {n} h', { n: Math.floor(h) })
  return t('scaduta da {n} min', { n: Math.max(1, Math.floor(h * 60)) })
}

/** How a step reads: a tone, a short text for the table and a longer one for the detail. */
function vistaPasso(p: Nis2IncidentStep, adesso: number): { tono: Tono; breve: string; esteso: string } {
  const ore = oreAllaScadenza(p, adesso)
  switch (statoOra(p, adesso)) {
    case 'non_dovuto':
      return { tono: 'muted', breve: t('non dovuta'), esteso: t('non dovuta') }
    case 'inviato':
      return { tono: 'ok', breve: t('inviata'), esteso: t('inviata nei termini') }
    case 'inviato_in_ritardo':
      return { tono: 'warn', breve: t('inviata in ritardo'), esteso: t('inviata in ritardo') }
    case 'mancante':
      return { tono: 'error', breve: t('non inviata'), esteso: t('non inviata: incidente chiuso senza registrarne l’invio') }
    case 'scaduto': {
      const testo = ore !== null ? scadutaDa(ore) : t('scaduta')
      return { tono: 'error', breve: testo, esteso: testo }
    }
    default: {
      if (ore === null) return { tono: 'info', breve: t('in corso'), esteso: t('in corso') }
      return { tono: ore <= SOGLIA_ORE[p.step] ? 'warn' : 'info', breve: fra(ore), esteso: fra(ore) }
    }
  }
}

/** A stored instant as the value of a datetime-local input (browser timezone). */
function perInput(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const due = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${due(d.getMonth() + 1)}-${due(d.getDate())}T${due(d.getHours())}:${due(d.getMinutes())}`
}

/** A datetime-local value as an ISO instant, read in the browser timezone. */
function daInput(valore: string): string | null {
  const d = new Date(valore)
  return valore && !Number.isNaN(d.getTime()) ? d.toISOString() : null
}

// --------------------------------------------------------------- the KPIs

interface Indicatori {
  aperti: number
  daValutare: number
  scaduti: number
  prossimo: { inc: Nis2Incident; passo: Nis2IncidentStep; ore: number } | null
  chiusi12: number
}

function indicatori(lista: Nis2Incident[], adesso: number): Indicatori {
  const aperti = lista.filter((i) => i.status !== 'chiuso')
  let scaduti = 0
  let prossimo: Indicatori['prossimo'] = null
  for (const inc of aperti) {
    for (const passo of inc.steps) {
      const stato = statoOra(passo, adesso)
      if (stato === 'scaduto') scaduti += 1
      if (stato !== 'in_corso') continue
      const ore = oreAllaScadenza(passo, adesso)
      if (ore !== null && (!prossimo || ore < prossimo.ore)) prossimo = { inc, passo, ore }
    }
  }
  const unAnnoFa = adesso - 365 * 24 * ORA
  return {
    aperti: aperti.length,
    daValutare: aperti.filter((i) => i.significant === null).length,
    scaduti,
    prossimo,
    chiusi12: lista.filter((i) => i.status === 'chiuso' && i.closed_at && Date.parse(i.closed_at) >= unAnnoFa).length,
  }
}

// ------------------------------------------------------------- the register

export function IncidentiNis2({ scrive }: { scrive: boolean }) {
  const elenco = useAsync(() => api.nis2Incidents(), [])
  const servizi = useAsync(() => api.services(), [])
  const adesso = useAdesso()
  const [nuovo, setNuovo] = useState(false)
  const [aperto, setAperto] = useState<string | null>(null)

  const incidenti = useMemo(() => elenco.data ?? [], [elenco.data])
  const k = useMemo(() => indicatori(incidenti, adesso), [incidenti, adesso])
  const ricarica = () => elenco.reload()
  const commuta = (id: string) => setAperto((a) => (a === id ? null : id))

  const colonne: Column<Nis2Incident>[] = [
    { key: 'reference', header: t('Riferimento'), mono: true, nowrap: true },
    { key: 'title', header: t('Titolo'), value: (i) => i.title },
    {
      key: 'status', header: t('Stato'), value: (i) => nomeStato(i.status),
      render: (i) => <Badge tono={tonoStato(i.status)}>{nomeStato(i.status)}</Badge>,
    },
    {
      key: 'severity', header: t('Gravità'), value: (i) => nomeGravita(i.severity),
      render: (i) => <Badge tono={tonoGravita(i.severity)}>{nomeGravita(i.severity)}</Badge>,
    },
    {
      key: 'significant', header: t('Significatività'), value: (i) => nomeSignificato(i.significant),
      render: (i) => <Badge tono={tonoSignificato(i.significant)}>{nomeSignificato(i.significant)}</Badge>,
    },
    {
      key: 'steps', header: t('Notifiche a CSIRT Italia'), sort: false, filter: false,
      value: (i) => i.steps.map((p) => `${nomePasso(p.step)}: ${vistaPasso(p, adesso).breve}`).join(' · '),
      render: (i) => <OrologiCompatti inc={i} adesso={adesso} />,
    },
    {
      key: 'aware_at', header: t('Presa di conoscenza'), type: 'date', value: (i) => i.aware_at,
      render: (i) => formatDateTime(i.aware_at),
    },
    { key: 'csirt_reference', header: t('Riferimento CSIRT'), mono: true, value: (i) => i.csirt_reference },
    {
      key: 'azioni', header: '', sort: false, searchable: false,
      render: (i) => (
        <button type="button" className="link" onClick={(e) => { e.stopPropagation(); commuta(i.id) }}>
          {aperto === i.id ? t('Chiudi') : t('Dettagli')}
        </button>
      ),
    },
  ]

  const prossimo = k.prossimo
  return (
    <>
      <div className="stats">
        <StatCard
          label={t('Incidenti aperti')}
          value={elenco.data ? k.aperti : '—'}
          tone={k.aperti > 0 ? 'warn' : 'ok'}
          hint={k.daValutare > 0 ? t('{n} con significatività da valutare', { n: k.daValutare }) : undefined}
        />
        <StatCard
          label={t('Notifiche scadute')}
          value={elenco.data ? k.scaduti : '—'}
          tone={k.scaduti > 0 ? 'error' : 'ok'}
          hint={t('Passi non inviati a CSIRT Italia oltre il termine')}
        />
        <StatCard
          label={t('Prossima scadenza')}
          value={prossimo ? fra(prossimo.ore) : '—'}
          tone={prossimo ? (prossimo.ore < 6 ? 'error' : prossimo.ore < 24 ? 'warn' : undefined) : undefined}
          hint={prossimo ? `${prossimo.inc.reference} · ${nomePasso(prossimo.passo.step)}` : t('Nessuna notifica in attesa')}
          onClick={prossimo ? () => setAperto(prossimo.inc.id) : undefined}
        />
        <StatCard
          label={t('Chiusi negli ultimi 12 mesi')}
          value={elenco.data ? k.chiusi12 : '—'}
        />
      </div>

      {nuovo && scrive && (
        <NuovoIncidente
          servizi={servizi.data}
          onAnnulla={() => setNuovo(false)}
          onCreato={async (creato) => {
            await ricarica()
            setNuovo(false)
            setAperto(creato.id)
          }}
        />
      )}

      <Card
        title={t('Registro degli incidenti')}
        actions={scrive && !nuovo ? <button type="button" onClick={() => setNuovo(true)}>{t('Registra incidente')}</button> : undefined}
      >
        <GuidaCsirt />
        <ErrorBanner error={elenco.error} />
        {elenco.loading && !elenco.data ? <Spinner /> : incidenti.length === 0 ? (
          <EmptyState
            title={t('Nessun incidente registrato')}
            hint={t('Registra l’incidente appena se ne ha conoscenza: da quel momento corrono le 24 ore per la pre-notifica.')}
          />
        ) : (
          <DataTable
            id="nis2-incidenti"
            rows={incidenti}
            columns={colonne}
            rowKey={(i) => i.id}
            conteggio={{ nome: t('incidenti') }}
            onRowClick={(i) => commuta(i.id)}
            isSelected={(i) => i.id === aperto}
            expanded={(i) => i.id === aperto}
            expand={(i) => (
              <DettaglioIncidente inc={i} adesso={adesso} scrive={scrive} servizi={servizi.data} onCambiato={ricarica} />
            )}
          />
        )}
      </Card>
    </>
  )
}

/** The three clocks of a row, one line each. */
function OrologiCompatti({ inc, adesso }: { inc: Nis2Incident; adesso: number }) {
  if (inc.steps.every((p) => p.state === 'non_dovuto' && !p.done_at)) {
    return <Badge tono="muted">{t('nessuna notifica dovuta')}</Badge>
  }
  return (
    <div style={{ display: 'grid', gap: 2 }}>
      {inc.steps.map((p) => {
        const v = vistaPasso(p, adesso)
        return <Badge key={p.step} tono={v.tono}>{nomePasso(p.step)}: {v.breve}</Badge>
      })}
    </div>
  )
}

/** What CSIRT Italia expects at each step, and who gets the reminders. */
function GuidaCsirt() {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
        {PASSI.map((p, n) => (
          <div key={p} style={{ borderLeft: '3px solid var(--divider)', paddingLeft: 10 }}>
            <strong>{n + 1}. {nomePasso(p)}</strong> <span className="text-muted eagle-piccolo">{terminePasso(p)}</span>
            <div className="field__hint" style={{ marginTop: 2 }}>{cosaInviare(p)}</div>
          </div>
        ))}
      </div>
      <p className="field__hint" style={{ marginBottom: 0 }}>
        {t('I promemoria via e-mail vanno al responsabile dell’incidente, al punto di contatto NIS2 e ai destinatari NIS2 del tenant: 6 h prima della pre-notifica, 12 h prima della notifica, 7 giorni prima della relazione finale e a ogni scadenza. Un incidente non ancora valutato fa correre gli orologi comunque.')}
      </p>
    </div>
  )
}

// ------------------------------------------------------------ the form

interface Campi {
  title: string
  description: string
  status: Nis2Incident['status']
  severity: Nis2Incident['severity']
  significant: Significato
  significance_reason: string
  suspected_malicious: boolean
  cross_border: boolean
  csirt_reference: string
  owner: string
  owner_email: string
  services: Rif[]
  cis: Rif[]
}

const CAMPI_VUOTI: Campi = {
  title: '', description: '', status: 'aperto', severity: 'medium', significant: '', significance_reason: '',
  suspected_malicious: false, cross_border: false, csirt_reference: '', owner: '', owner_email: '',
  services: [], cis: [],
}

function campiDa(inc: Nis2Incident): Campi {
  return {
    title: inc.title, description: inc.description, status: inc.status, severity: inc.severity,
    significant: inc.significant === null ? '' : inc.significant ? 'si' : 'no',
    significance_reason: inc.significance_reason, suspected_malicious: inc.suspected_malicious,
    cross_border: inc.cross_border, csirt_reference: inc.csirt_reference, owner: inc.owner,
    owner_email: inc.owner_email, services: inc.affected_services, cis: inc.affected_cis,
  }
}

function significato(s: Significato): boolean | null {
  return s === '' ? null : s === 'si'
}

const idDi = (r: Rif[]) => r.map((x) => x.id).sort().join(',')

/** The fields that differ from `base`, as an update body. */
function modifiche(f: Campi, base: Campi): Nis2IncidentInput {
  const out: Nis2IncidentInput = {}
  const testi = ['title', 'description', 'significance_reason', 'csirt_reference', 'owner', 'owner_email'] as const
  for (const c of testi) if (f[c].trim() !== base[c].trim()) out[c] = f[c].trim()
  if (f.status !== base.status) out.status = f.status
  if (f.severity !== base.severity) out.severity = f.severity
  if (f.significant !== base.significant) out.significant = significato(f.significant)
  if (f.suspected_malicious !== base.suspected_malicious) out.suspected_malicious = f.suspected_malicious
  if (f.cross_border !== base.cross_border) out.cross_border = f.cross_border
  if (idDi(f.services) !== idDi(base.services)) out.affected_services = f.services.map((x) => x.id)
  if (idDi(f.cis) !== idDi(base.cis)) out.affected_cis = f.cis.map((x) => x.id)
  return out
}

function NuovoIncidente({ servizi, onAnnulla, onCreato }: {
  servizi: BusinessService[] | null
  onAnnulla: () => void
  onCreato: (creato: Nis2Incident) => Promise<void>
}) {
  const [rilevato, setRilevato] = useState(() => perInput(new Date().toISOString()))
  const [conosciuto, setConosciuto] = useState(rilevato)
  const [f, setF] = useState<Campi>(CAMPI_VUOTI)
  const [errore, setErrore] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  async function registra() {
    setErrore(null)
    const da = daInput(rilevato)
    const cc = daInput(conosciuto)
    if (!da || !cc) {
      setErrore(new Error(t('Indica data e ora del rilevamento e della presa di conoscenza.')))
      return
    }
    if (cc < da) {
      setErrore(new Error(t('La presa di conoscenza non può precedere il rilevamento.')))
      return
    }
    setBusy(true)
    try {
      const creato = await api.openNis2Incident({
        title: f.title.trim(),
        description: f.description.trim(),
        detected_at: da,
        aware_at: cc,
        severity: f.severity,
        significant: significato(f.significant),
        significance_reason: f.significance_reason.trim(),
        suspected_malicious: f.suspected_malicious,
        cross_border: f.cross_border,
        affected_services: f.services.map((x) => x.id),
        affected_cis: f.cis.map((x) => x.id),
        owner: f.owner.trim(),
        owner_email: f.owner_email.trim(),
      })
      await onCreato(creato)
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title={t('Registra incidente')}>
      <ErrorBanner error={errore} />
      <div className="eagle-report__riga">
        <label className="field">
          <span>{t('Rilevato il')}</span>
          <input
            type="datetime-local" style={STILE_DATA_ORA} value={rilevato}
            onChange={(e) => {
              // Awareness follows detection until it is set on its own.
              if (conosciuto === rilevato) setConosciuto(e.target.value)
              setRilevato(e.target.value)
            }}
          />
          <div className="field__hint">{t('Quando l’evento è stato rilevato (allarme, segnalazione, anomalia).')}</div>
        </label>
        <label className="field">
          <span>{t('Presa di conoscenza')}</span>
          <input type="datetime-local" style={STILE_DATA_ORA} value={conosciuto} onChange={(e) => setConosciuto(e.target.value)} />
          <div className="field__hint">
            {t('Da qui partono gli orologi dell’art. 25: pre-notifica entro 24 h, notifica entro 72 h, relazione finale entro un mese dalla notifica.')}
          </div>
        </label>
      </div>
      <CampiIncidente f={f} cambia={(p) => setF((x) => ({ ...x, ...p }))} servizi={servizi} prefisso="nis2-inc-nuovo" scheda={false} />
      <div className="eagle-form__azioni">
        <button type="button" disabled={busy || !f.title.trim()} onClick={() => void registra()}>
          {busy ? t('Salvo…') : t('Registra incidente')}
        </button>
        <button type="button" className="secondary" disabled={busy} onClick={onAnnulla}>{t('Annulla')}</button>
      </div>
    </Card>
  )
}

/** The incident's fields; `scheda` adds the ones that only make sense once it exists. */
function CampiIncidente({ f, cambia, servizi, prefisso, scheda }: {
  f: Campi
  cambia: (p: Partial<Campi>) => void
  servizi: BusinessService[] | null
  prefisso: string
  scheda: boolean
}) {
  return (
    <>
      <div className="eagle-report__riga">
        <label className="field" style={{ flex: 2 }}>
          <span>{t('Titolo')}</span>
          <input type="text" value={f.title} onChange={(e) => cambia({ title: e.target.value })}
            placeholder={t('es. ransomware sui file server, indisponibilità del portale')} maxLength={300} />
        </label>
        <label className="field">
          <span>{t('Gravità')}</span>
          <select value={f.severity} onChange={(e) => cambia({ severity: e.target.value as Campi['severity'] })}>
            {(['low', 'medium', 'high', 'critical'] as const).map((s) => <option key={s} value={s}>{nomeGravita(s)}</option>)}
          </select>
        </label>
        {scheda && (
          <label className="field">
            <span>{t('Stato')}</span>
            <select value={f.status} onChange={(e) => cambia({ status: e.target.value as Campi['status'] })}>
              {(['aperto', 'contenuto', 'chiuso'] as const).map((s) => <option key={s} value={s}>{nomeStato(s)}</option>)}
            </select>
          </label>
        )}
      </div>
      <label className="field">
        <span>{t('Descrizione')}</span>
        <textarea rows={3} value={f.description} onChange={(e) => cambia({ description: e.target.value })}
          placeholder={t('cosa è successo, cosa si è visto, cosa si è fatto finora')} />
      </label>
      <div className="eagle-report__riga">
        <label className="field">
          <span>{t('Significatività')}</span>
          <select value={f.significant} onChange={(e) => cambia({ significant: e.target.value as Significato })}>
            <option value="">{t('da valutare')}</option>
            <option value="si">{t('significativo')}</option>
            <option value="no">{t('non significativo')}</option>
          </select>
        </label>
        <label className="field" style={{ flex: 2 }}>
          <span>{t('Motivazione')}</span>
          <input type="text" value={f.significance_reason} onChange={(e) => cambia({ significance_reason: e.target.value })}
            placeholder={t('perché è o non è significativo')} />
        </label>
      </div>
      <p className="field__hint" style={{ marginTop: -8 }}>
        {t('Art. 25: è significativo l’incidente che ha causato o può causare una grave perturbazione operativa dei servizi o perdite finanziarie per il soggetto, oppure danni materiali o immateriali considerevoli ad altre persone fisiche o giuridiche. Finché non è valutato gli orologi corrono comunque: meglio una pre-notifica non necessaria che una notifica in ritardo.')}
      </p>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        <div className="checkbox">
          <input id={`${prefisso}-malevolo`} type="checkbox" checked={f.suspected_malicious}
            onChange={(e) => cambia({ suspected_malicious: e.target.checked })} />
          <label htmlFor={`${prefisso}-malevolo`}>
            {t('Sospetta azione illegittima o malevola')}
            <small>{t('Da dichiarare già nella pre-notifica.')}</small>
          </label>
        </div>
        <div className="checkbox">
          <input id={`${prefisso}-transfrontaliero`} type="checkbox" checked={f.cross_border}
            onChange={(e) => cambia({ cross_border: e.target.checked })} />
          <label htmlFor={`${prefisso}-transfrontaliero`}>
            {t('Possibile impatto transfrontaliero')}
            <small>{t('Coinvolge altri Stati membri: da dichiarare già nella pre-notifica.')}</small>
          </label>
        </div>
      </div>
      <div className="eagle-report__riga" style={{ alignItems: 'flex-start' }}>
        <SceltaServizi id={`${prefisso}-servizi`} servizi={servizi} scelti={f.services} onCambia={(services) => cambia({ services })} />
        <SceltaCi id={`${prefisso}-ci`} scelti={f.cis} onCambia={(cis) => cambia({ cis })} />
      </div>
      <div className="eagle-report__riga">
        <label className="field">
          <span>{t('Responsabile')}</span>
          <input type="text" value={f.owner} onChange={(e) => cambia({ owner: e.target.value })} maxLength={200} />
        </label>
        <label className="field">
          <span>{t('E-mail del responsabile')}</span>
          <input type="email" value={f.owner_email} onChange={(e) => cambia({ owner_email: e.target.value })} maxLength={320} />
          <div className="field__hint">{t('Riceve i promemoria delle scadenze insieme al punto di contatto NIS2.')}</div>
        </label>
        {scheda && (
          <label className="field">
            <span>{t('Riferimento CSIRT')}</span>
            <input type="text" className="mono" value={f.csirt_reference} onChange={(e) => cambia({ csirt_reference: e.target.value })} maxLength={100} />
            <div className="field__hint">{t('Il numero che CSIRT Italia assegna alla segnalazione.')}</div>
          </label>
        )}
      </div>
    </>
  )
}

function Chips({ voci, onTogli }: { voci: Rif[]; onTogli: (id: string) => void }) {
  if (voci.length === 0) return null
  return (
    <div style={{ marginBottom: 6 }}>
      {voci.map((v) => (
        <span key={v.id} className="chip">
          {v.name}{' '}
          <button type="button" className="link" aria-label={t('Togli {nome}', { nome: v.name })} onClick={() => onTogli(v.id)}>×</button>
        </span>
      ))}
    </div>
  )
}

function SceltaServizi({ id, servizi, scelti, onCambia }: {
  id: string
  servizi: BusinessService[] | null
  scelti: Rif[]
  onCambia: (r: Rif[]) => void
}) {
  const liberi = (servizi ?? []).filter((s) => !scelti.some((x) => x.id === s.id))
  return (
    <div className="field">
      <label htmlFor={id}>{t('Servizi coinvolti')}</label>
      <Chips voci={scelti} onTogli={(v) => onCambia(scelti.filter((x) => x.id !== v))} />
      {servizi === null ? (
        <div className="field__hint">{t('Elenco dei servizi non disponibile.')}</div>
      ) : (
        <select
          id={id} value="" disabled={liberi.length === 0}
          onChange={(e) => {
            const s = liberi.find((x) => x.id === e.target.value)
            if (s) onCambia([...scelti, { id: s.id, name: s.name }])
          }}
        >
          <option value="">{liberi.length === 0 ? t('Nessun altro servizio') : t('Aggiungi un servizio…')}</option>
          {liberi.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      )}
    </div>
  )
}

function SceltaCi({ id, scelti, onCambia }: { id: string; scelti: Rif[]; onCambia: (r: Rif[]) => void }) {
  const [q, setQ] = useState('')
  const [risultati, setRisultati] = useState<ConfigurationItem[]>([])
  useEffect(() => {
    const testo = q.trim()
    if (testo.length < 2) {
      setRisultati([])
      return
    }
    let vivo = true
    const timer = window.setTimeout(() => {
      api.cis({ q: testo, limit: 10 })
        .then((r) => { if (vivo) setRisultati(r) })
        .catch(() => { if (vivo) setRisultati([]) })
    }, 250)
    return () => {
      vivo = false
      window.clearTimeout(timer)
    }
  }, [q])
  const proposti = risultati.filter((c) => !scelti.some((x) => x.id === c.id))
  return (
    <div className="field">
      <label htmlFor={id}>{t('CI coinvolti')}</label>
      <Chips voci={scelti} onTogli={(v) => onCambia(scelti.filter((x) => x.id !== v))} />
      <input id={id} type="search" value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off"
        placeholder={t('Cerca un CI per nome o indirizzo')} />
      {/* In flow, not floating: inside a table row a floating list would be clipped. */}
      {proposti.length > 0 && (
        <ul className="card" style={{ margin: '4px 0 0', padding: 4, listStyle: 'none', maxHeight: 240, overflowY: 'auto' }}>
          {proposti.map((c) => (
            <li key={c.id}>
              <button
                type="button" className="link" style={{ width: '100%', textAlign: 'left', padding: '6px 8px' }}
                onClick={() => {
                  onCambia([...scelti, { id: c.id, name: c.name ?? c.primary_key }])
                  setQ('')
                  setRisultati([])
                }}
              >
                {c.name ?? c.primary_key} <small className="text-muted">{c.ci_class ?? c.ci_type}</small>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ----------------------------------------------------------- the detail

function DettaglioIncidente({ inc, adesso, scrive, servizi, onCambiato }: {
  inc: Nis2Incident
  adesso: number
  scrive: boolean
  servizi: BusinessService[] | null
  onCambiato: () => Promise<void>
}) {
  const [errore, setErrore] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  const [modifica, setModifica] = useState(false)

  async function aggiorna(corpo: Nis2IncidentInput): Promise<boolean> {
    setBusy(true)
    setErrore(null)
    try {
      await api.updateNis2Incident(inc.id, corpo)
      await onCambiato()
      return true
    } catch (e) {
      setErrore(e)
      return false
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ padding: '8px 4px', display: 'grid', gap: 20 }}>
      <ErrorBanner error={errore} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24, alignItems: 'start' }}>
        <PassiIncidente inc={inc} adesso={adesso} scrive={scrive} busy={busy} aggiorna={aggiorna} />
        {modifica ? (
          <ModificaIncidente inc={inc} servizi={servizi} busy={busy} aggiorna={aggiorna} onChiudi={() => setModifica(false)} />
        ) : (
          <Riepilogo inc={inc} scrive={scrive} onModifica={() => setModifica(true)} />
        )}
      </div>
      <Cronologia inc={inc} scrive={scrive} busy={busy} aggiorna={aggiorna} />
    </div>
  )
}

function PassiIncidente({ inc, adesso, scrive, busy, aggiorna }: {
  inc: Nis2Incident
  adesso: number
  scrive: boolean
  busy: boolean
  aggiorna: (corpo: Nis2IncidentInput) => Promise<boolean>
}) {
  return (
    <div>
      <h4 style={{ margin: '0 0 8px' }}>{t('Notifiche a CSIRT Italia')}</h4>
      {inc.significant === false && (
        <div className="banner banner--info" style={{ marginBottom: 10 }}>
          {t('Valutato non significativo: nessuna notifica dovuta a CSIRT Italia.')}
        </div>
      )}
      {inc.significant === null && inc.status !== 'chiuso' && (
        <div className="banner banner--warn" style={{ marginBottom: 10 }}>
          {t('Significatività da valutare: gli orologi corrono comunque.')}
        </div>
      )}
      <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 12 }}>
        {inc.steps.map((p, n) => (
          <PassoIncidente key={p.step} n={n + 1} inc={inc} passo={p} adesso={adesso} scrive={scrive} busy={busy} aggiorna={aggiorna} />
        ))}
      </ol>
    </div>
  )
}

function PassoIncidente({ n, inc, passo, adesso, scrive, busy, aggiorna }: {
  n: number
  inc: Nis2Incident
  passo: Nis2IncidentStep
  adesso: number
  scrive: boolean
  busy: boolean
  aggiorna: (corpo: Nis2IncidentInput) => Promise<boolean>
}) {
  const [altraOra, setAltraOra] = useState(false)
  const [quando, setQuando] = useState(() => perInput(new Date().toISOString()))
  const v = vistaPasso(passo, adesso)
  const dovuto = passo.state !== 'non_dovuto'
  const notificaFatta = inc.steps.some((p) => p.step === 'notification' && p.done_at)

  async function segna(iso: string | null) {
    const corpo: Nis2IncidentInput = {}
    corpo[CAMPO_PASSO[passo.step]] = iso
    if (iso === null) corpo.note = ANNULLATO[passo.step]
    if (await aggiorna(corpo)) setAltraOra(false)
  }

  return (
    <li style={{ borderLeft: `3px solid ${COLORE_TONO[v.tono]}`, paddingLeft: 10 }}>
      <div>
        <strong>{n}. {nomePasso(passo.step)}</strong>{' '}
        <span className="text-muted eagle-piccolo">{terminePasso(passo.step)}</span>
      </div>
      <div style={{ marginTop: 2 }}>
        <Badge tono={v.tono}>{v.esteso}</Badge>
        {passo.due_at && <span className="text-muted"> · {t('termine')}: {formatDateTime(passo.due_at)}</span>}
      </div>
      {passo.done_at && (
        <div className="text-muted">{t('Inviata il {quando}', { quando: formatDateTime(passo.done_at) })}</div>
      )}
      {passo.step === 'final_report' && dovuto && !notificaFatta && (
        <div className="field__hint">{t('Calcolato dal termine della notifica finché l’invio della notifica non è registrato.')}</div>
      )}
      {dovuto && !passo.done_at && <div className="field__hint">{cosaInviare(passo.step)}</div>}
      {scrive && (dovuto || passo.done_at) && (
        <div className="eagle-form__azioni" style={{ marginTop: 8, alignItems: 'center' }}>
          {passo.done_at ? (
            <button type="button" className="secondary" disabled={busy} onClick={() => void segna(null)}>
              {t('Annulla l’invio')}
            </button>
          ) : altraOra ? (
            <>
              <input
                type="datetime-local" style={{ ...STILE_DATA_ORA, width: 'auto' }} value={quando}
                onChange={(e) => setQuando(e.target.value)} aria-label={t('Data e ora dell’invio')}
              />
              <button type="button" disabled={busy || !daInput(quando)} onClick={() => void segna(daInput(quando))}>
                {t('Registra l’invio')}
              </button>
              <button type="button" className="secondary" disabled={busy} onClick={() => setAltraOra(false)}>{t('Annulla')}</button>
            </>
          ) : (
            <>
              <button type="button" disabled={busy} onClick={() => void segna(new Date().toISOString())}>
                {t('Segna come inviata')}
              </button>
              <button type="button" className="link" disabled={busy} onClick={() => setAltraOra(true)}>
                {t('inviata a un’altra ora…')}
              </button>
            </>
          )}
        </div>
      )}
    </li>
  )
}

function Riepilogo({ inc, scrive, onModifica }: { inc: Nis2Incident; scrive: boolean; onModifica: () => void }) {
  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 8 }}>
        <h4 style={{ margin: 0 }}>{inc.reference} · {inc.title}</h4>
        {scrive && <button type="button" className="link" onClick={onModifica}>{t('Modifica i dati')}</button>}
      </div>
      {inc.description && <p style={{ whiteSpace: 'pre-wrap', marginTop: 0 }}>{inc.description}</p>}
      <dl className="kv" style={{ gap: '6px 20px', margin: 0 }}>
        <dt>{t('Stato')}</dt>
        <dd>
          <Badge tono={tonoStato(inc.status)}>{nomeStato(inc.status)}</Badge>
          {inc.closed_at && <span className="text-muted"> · {formatDateTime(inc.closed_at)}</span>}
        </dd>
        <dt>{t('Gravità')}</dt>
        <dd><Badge tono={tonoGravita(inc.severity)}>{nomeGravita(inc.severity)}</Badge></dd>
        <dt>{t('Significatività')}</dt>
        <dd>
          <Badge tono={tonoSignificato(inc.significant)}>{nomeSignificato(inc.significant)}</Badge>
          {inc.significance_reason && <div className="field__hint" style={{ whiteSpace: 'pre-wrap' }}>{inc.significance_reason}</div>}
        </dd>
        <dt>{t('Rilevato il')}</dt>
        <dd>{formatDateTime(inc.detected_at)}</dd>
        <dt>{t('Presa di conoscenza')}</dt>
        <dd>{formatDateTime(inc.aware_at)}</dd>
        <dt>{t('Sospetta azione illegittima o malevola')}</dt>
        <dd>{inc.suspected_malicious ? t('sì') : t('no')}</dd>
        <dt>{t('Possibile impatto transfrontaliero')}</dt>
        <dd>{inc.cross_border ? t('sì') : t('no')}</dd>
        <dt>{t('Servizi coinvolti')}</dt>
        <dd>
          {inc.affected_services.length === 0 ? '—' : inc.affected_services.map((s, i) => (
            <span key={s.id}>{i > 0 && ', '}<Link to={`/services/${s.id}`}>{s.name}</Link></span>
          ))}
        </dd>
        <dt>{t('CI coinvolti')}</dt>
        <dd>
          {inc.affected_cis.length === 0 ? '—' : inc.affected_cis.map((c, i) => (
            <span key={c.id}>{i > 0 && ', '}<Link to={`/cis/${c.id}`}>{c.name}</Link></span>
          ))}
        </dd>
        <dt>{t('Responsabile')}</dt>
        <dd>
          {inc.owner || '—'}
          {inc.owner_email && <> · <a href={`mailto:${inc.owner_email}`}>{inc.owner_email}</a></>}
        </dd>
        <dt>{t('Riferimento CSIRT')}</dt>
        <dd className="mono">{inc.csirt_reference || '—'}</dd>
        <dt>{t('Registrato da')}</dt>
        <dd>{inc.created_by || '—'}{inc.created_at && <span className="text-muted"> · {formatDateTime(inc.created_at)}</span>}</dd>
      </dl>
    </div>
  )
}

function ModificaIncidente({ inc, servizi, busy, aggiorna, onChiudi }: {
  inc: Nis2Incident
  servizi: BusinessService[] | null
  busy: boolean
  aggiorna: (corpo: Nis2IncidentInput) => Promise<boolean>
  onChiudi: () => void
}) {
  const [f, setF] = useState<Campi>(() => campiDa(inc))
  // Compared with the incident as last loaded, so only real changes are sent.
  const corpo = modifiche(f, campiDa(inc))
  const cambiato = Object.keys(corpo).length > 0
  const daInviare = inc.steps.some((p) => (p.state === 'in_corso' || p.state === 'scaduto') && !p.done_at)

  return (
    <div>
      <h4 style={{ margin: '0 0 8px' }}>{inc.reference} · {t('Modifica i dati')}</h4>
      <CampiIncidente f={f} cambia={(p) => setF((x) => ({ ...x, ...p }))} servizi={servizi} prefisso={`nis2-inc-${inc.id}`} scheda />
      {f.status === 'chiuso' && inc.status !== 'chiuso' && daInviare && (
        <div className="banner banner--warn" style={{ marginBottom: 12 }}>
          {t('Chiudendo l’incidente le notifiche non registrate restano «non inviate» nel registro.')}
        </div>
      )}
      <div className="eagle-form__azioni">
        <button type="button" disabled={busy || !cambiato || !f.title.trim()}
          onClick={async () => { if (await aggiorna(corpo)) onChiudi() }}>
          {busy ? t('Salvo…') : t('Salva le modifiche')}
        </button>
        <button type="button" className="secondary" disabled={busy} onClick={onChiudi}>{t('Annulla')}</button>
      </div>
    </div>
  )
}

function Cronologia({ inc, scrive, busy, aggiorna }: {
  inc: Nis2Incident
  scrive: boolean
  busy: boolean
  aggiorna: (corpo: Nis2IncidentInput) => Promise<boolean>
}) {
  const [nota, setNota] = useState('')
  return (
    <div>
      <h4 style={{ margin: '0 0 8px' }}>{t('Cronologia')}</h4>
      {scrive && (
        <div className="eagle-report__riga" style={{ marginBottom: 12 }}>
          <label className="field" style={{ flex: 1, marginBottom: 0 }}>
            <span>{t('Nuova nota')}</span>
            <input type="text" value={nota} onChange={(e) => setNota(e.target.value)} maxLength={4000}
              placeholder={t('cosa si è scoperto, cosa si è deciso, cosa si è comunicato')} />
          </label>
          <button type="button" disabled={busy || !nota.trim()}
            onClick={async () => { if (await aggiorna({ note: nota.trim() })) setNota('') }}>
            {t('Aggiungi nota')}
          </button>
        </div>
      )}
      {inc.timeline.length === 0 ? <p className="text-muted">{t('Nessuna voce.')}</p> : (
        <ul className="contratto-storico">
          {inc.timeline.map((v, i) => (
            <li key={`${v.at}-${i}`}>
              <div className="text-muted eagle-piccolo">{formatDateTime(v.at)}{v.by && ` · ${v.by}`}</div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{testoVoce(v.text)}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
